import React, { useMemo } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";

const EnptEdrTable = ({ edrs, reportId }) => {
  const data = Array.isArray(edrs) ? edrs : edrs ? [edrs] : [];

  const { tableData, columns } = useMemo(() => {
    if (data.length === 0) return { tableData: [], columns: [] };

    // 1. Get unique enterprises and sort them (case-insensitive)
    const uniqueEnterprises = [...new Set(data.map(d => d.enterprise).filter(Boolean))].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

    // 2. Get unique dates (sort descending)
    const uniqueDates = [...new Set(data.map(d => d.test_date).filter(Boolean))].sort((a, b) => new Date(b) - new Date(a));
    const hasDates = uniqueDates.length > 0;
    const dates = hasDates ? uniqueDates : ['Delivery Ratio'];

    // 3. Group by date and report_id and calculate enterprise averages
    const dateMap = {};
    const enterpriseAvgMap = {}; // Tracks sum and count for each enterprise across all dates
    
    data.forEach(item => {
      // If the report uses date-based data, ignore legacy/corrupt records with no date
      if (hasDates && !item.test_date) return;

      const dateStr = item.test_date || 'Delivery Ratio';
      const dateKey = item.report_id ? `${dateStr}_${item.report_id}` : dateStr;
      
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { row_key: dateKey, test_date: dateStr, totalRatio: 0, count: 0 };
      }
      // Guarantee the row is marked with the current reportId if any of its EDRs belong to it
      if (!dateMap[dateKey].report_id || item.report_id === reportId) {
        dateMap[dateKey].report_id = item.report_id;
      }
      
      const ent = item.enterprise;
      if (ent) {
        dateMap[dateKey][ent] = item.delivery_ratio;
        
        if (item.delivery_ratio !== null && item.delivery_ratio !== undefined) {
          const val = Number(item.delivery_ratio);
          
          // For date row average
          dateMap[dateKey].totalRatio += val;
          dateMap[dateKey].count += 1;
          
          // For enterprise average across all dates
          if (!enterpriseAvgMap[ent]) enterpriseAvgMap[ent] = { total: 0, count: 0 };
          enterpriseAvgMap[ent].total += val;
          enterpriseAvgMap[ent].count += 1;
        }
      }
    });

    // 4. Calculate averages, build row data, and sort descending by date
    const tableData = Object.values(dateMap).map(row => {
        row.avg_ratio = row.count > 0 ? (row.totalRatio / row.count).toFixed(4) : null;
        return row;
      })
      .sort((a, b) => {
        const timeA = new Date(a.test_date).getTime();
        const timeB = new Date(b.test_date).getTime();
        if (!isNaN(timeA) && !isNaN(timeB)) return timeB - timeA;
        return 0; // Fallback for legacy non-date strings
      });

    // 5. Build summary row for Avg Ratio (%) per enterprise
    const avgRow = { row_key: "avg_row", test_date: "Avg Ratio (%)" };
    let grandTotal = 0;
    let grandCount = 0;
    uniqueEnterprises.forEach(ent => {
      if (enterpriseAvgMap[ent] && enterpriseAvgMap[ent].count > 0) {
        avgRow[ent] = (enterpriseAvgMap[ent].total / enterpriseAvgMap[ent].count).toFixed(4);
        grandTotal += enterpriseAvgMap[ent].total;
        grandCount += enterpriseAvgMap[ent].count;
      } else {
        avgRow[ent] = null;
      }
    });
    avgRow.avg_ratio = grandCount > 0 ? (grandTotal / grandCount).toFixed(4) : null;
    
    // Add the summary row at the top of the table (right after headers)
    tableData.unshift(avgRow);

    // 6. Build columns
    const cols = [
      { 
        dataField: "test_date", 
        text: hasDates ? "Test Date" : "Data", 
        style: (cell, row) => row.report_id === reportId 
          ? { minWidth: "120px", fontWeight: "bold", backgroundColor: "yellow" } 
          : { minWidth: "120px", fontWeight: "bold" },
        sort: true 
      },
      ...uniqueEnterprises.map(ent => ({
        dataField: ent,
        text: ent,
        style: { minWidth: "120px" },
        formatter: (cell) => {
          if (cell === null || cell === undefined) return '-';
          const num = Number(cell) * 100;
          return Number.isInteger(num) ? `${num}%` : `${num.toFixed(1)}%`;
        }
      }))
    ];

    return { tableData, columns: cols };
  }, [data, reportId]);

  return (
    <div className="table-responsive">
      {tableData.length > 0 ? (
        <BootstrapTable
          keyField="row_key"
          data={tableData}
          columns={columns}
          filter={filterFactory()}
          classes="table table-bordered"
          wrapperClasses="table-responsive scrollable table_shadow_div"
        />
      ) : (
        <div className="text-center py-4">No Enterprise Delivery Ratio Data Available</div>
      )}
    </div>
  );
};

export default EnptEdrTable;
